import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabase/supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const isProduction = import.meta.env.PROD === true;

const initialPreviewMode = !isProduction && (
  import.meta.env.VITE_PREVIEW_MODE === 'true' ||
  (typeof window !== 'undefined' &&
    (window.localStorage?.getItem('saleswork_preview_mode') === 'true' ||
      window.location.search.includes('preview=1') ||
      window.location.hash.includes('preview=1')))
);

const previewUser = {
  user_id: 'preview-superadmin',
  email: 'preview@local.saleswork.no',
  name: 'Local SuperAdmin Preview',
  role: 'SuperAdmin',
  appMetadataRole: 'SuperAdmin',
  isSuperAdmin: true
};

const demoAccounts = [
  {
    id: 'demo-start',
    email: 'start.demo@saleswork.no',
    password: 'demo-start',
    name: 'Start Demo',
    plan: 'Start',
    planId: 'start',
    tenantName: 'Start Demo AS'
  },
  {
    id: 'demo-pro',
    email: 'vekst.demo@saleswork.no',
    password: 'demo-vekst',
    name: 'Vekst Demo',
    plan: 'Vekst',
    planId: 'pro',
    tenantName: 'Vekst Demo AS'
  },
  {
    id: 'demo-enterprise',
    email: 'enterprise.demo@saleswork.no',
    password: 'demo-enterprise',
    name: 'Enterprise Demo',
    plan: 'Enterprise',
    planId: 'enterprise'
  }
];

const getDisplayName = user => {
  const profile = user?.profile || user?.user_profile || {};
  const metadata = user?.user_metadata || {};
  const configuredName = [
    profile.full_name,
    profile.display_name,
    user?.full_name,
    user?.display_name,
    user?.fullName,
    metadata.full_name,
    metadata.display_name,
    metadata.name
  ].find(value => typeof value === 'string' && value.trim());

  if (configuredName) {
    return configuredName.trim();
  }

  const email = user?.email?.trim();
  if (!email) return 'Bruker';

  const localPart = email.split('@')[0]
    .replace(/[._-]+/g, ' ')
    .trim();

  if (!localPart) return email;

  return localPart
    .split(/\s+/)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [previewMode, setPreviewMode] = useState(initialPreviewMode);

  const handleAuthChange = session => {
    if (session) {
      const appMetadataRole = session.user.app_metadata?.role || '';
      const isSuperAdmin = appMetadataRole === 'SuperAdmin';

      if (!isProduction) {
        console.log('[AUTH CONTEXT ROLE CHECK]', {
          email: session.user.email,
          appMetadata: session.user.app_metadata,
          userMetadata: session.user.user_metadata,
          role: appMetadataRole,
          isSuperAdmin,
          hasUser: Boolean(session.user),
          hasSession: Boolean(session)
        });
      }

      setCurrentUser({
        user_id: session.user.id,
        email: session.user.email,
        name: getDisplayName(session.user),
        role: session.user.user_metadata?.role || 'Admin',
        appMetadataRole,
        isSuperAdmin
      });
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    setIsAuthenticated(false);
    setCurrentUser(null);
    setLoading(false);
  };

  useEffect(() => {
    if (isProduction && typeof localStorage !== 'undefined') {
      localStorage.removeItem('saleswork_preview_mode');
    }

    if (!isProduction) {
      console.log('[AUTH CONTEXT MODE]', {
        previewMode,
        isDev: import.meta.env.DEV,
        explicitPreviewEnv: import.meta.env.VITE_PREVIEW_MODE
      });
    }

    if (!isProduction && previewMode) {
      setCurrentUser(previewUser);
      setIsAuthenticated(true);
      setLoading(false);
      return undefined;
    }

    const initAuth = async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      handleAuthChange(session);
    };

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isProduction) {
        console.log('[AUTH CONTEXT STATE CHANGE]', {
          event,
          hasSession: Boolean(session),
          userEmail: session?.user?.email
        });
      }

      handleAuthChange(session);
    });

    initAuth();

    return () => subscription.unsubscribe();
  }, [previewMode]);

  const enterPreviewMode = () => {
    if (isProduction) {
      return {
        success: false,
        error: 'Preview mode er deaktivert i produksjon.'
      };
    }

    setPreviewMode(true);
    setCurrentUser(previewUser);
    setIsAuthenticated(true);
    setLoading(false);

    return { success: true };
  };

  const loginDemo = async (email, password) => {
    if (isProduction) {
      return {
        success: false,
        error: 'Demo-login er deaktivert i produksjon.'
      };
    }

    const account = demoAccounts.find(
      item => item.email === email.trim().toLowerCase() &&
        item.password === password
    );

    if (!account) {
      return {
        success: false,
        error: 'Demo-e-post eller passord er feil.'
      };
    }

    setPreviewMode(false);
    setCurrentUser({
      user_id: account.id,
      email: account.email,
      name: account.name,
      role: 'Customer',
      appMetadataRole: 'Customer',
      isSuperAdmin: false,
      isDemoUser: true,
      demoPlan: account.planId
    });
    setIsAuthenticated(true);
    setLoading(false);

    return {
      success: true,
      session: null,
      isSuperAdmin: false,
      isDemoUser: true,
      plan: account.plan,
      planId: account.planId
    };
  };

  const login = async (email, password) => {
    if (!isProduction && previewMode) {
      return {
        success: true,
        session: null,
        appMetadataRole: 'SuperAdmin',
        isSuperAdmin: true
      };
    }

    const normalizedEmail = email.trim().toLowerCase();

    if (!isProduction) {
      console.log('[AUTH CONTEXT] signInWithPassword calling for:', normalizedEmail);
    }

    try {
      const result = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password
      });

      if (!isProduction) {
        console.log('[AUTH CONTEXT ERROR RAW]', result.error);
      }

      if (result.error) {
        if (!isProduction) {
          console.log('[AUTH CONTEXT ERROR MESSAGE]', result.error.message);
          console.log('[AUTH CONTEXT ERROR STATUS]', result.error.status);
        }

        return {
          success: false,
          error: result.error.message,
          status: result.error.status
        };
      }

      const appMetadataRole = result.data.user?.app_metadata?.role || '';
      const isSuperAdmin = appMetadataRole === 'SuperAdmin';

      if (!isProduction) {
        console.log('[AUTH CONTEXT SUCCESS]', {
          userId: result.data.user?.id,
          hasUser: Boolean(result.data.user),
          hasSession: Boolean(result.data.session),
          appMetadata: result.data.user?.app_metadata,
          userMetadata: result.data.user?.user_metadata
        });
      }

      return {
        success: true,
        session: result.data.session,
        appMetadataRole,
        isSuperAdmin
      };
    } catch (error) {
      console.error('[AUTH CONTEXT CRASH]', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  const logout = async () => {
    if (!isProduction) {
      console.log('[AUTH CONTEXT LOGOUT]');
    }

    if (!isProduction && previewMode) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      return;
    }

    if (currentUser?.isDemoUser) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      return;
    }

    await supabase.auth.signOut();
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    previewMode,
    login,
    loginDemo,
    demoAccounts,
    logout,
    enterPreviewMode,
    appMetadataRole: currentUser?.appMetadataRole || '',
    isSuperAdmin: Boolean(currentUser?.isSuperAdmin),
    permissions: {
      canViewAll: true,
      canImportBrreg: true,
      canSettings: true,
      canViewCRM: true,
      canViewReports: true,
      canOutreach: true
    },
    themeSettings: {
      primaryColor: '#2563eb',
      sidebarColor: '#172554'
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;